import { useMutation } from '@tanstack/react-query';
import { HttpService, buildNavigationStack } from '@Services/index';
import { useBoundStore, OnboardingTypeEnum } from '@Store/index';
import { updateCustomerDataFromServer } from '@Services/init/util';
import { OnboardingFlowIdentifier } from '@Types/onboarding.types';
import { identifyLogRocketUser } from '@Services/analytics/logrocket.service';
import { evaluateRainguard } from '@Services/rainguard/rainguard.service';
import { identifyGA4User, setGA4UserProperties } from '@Services/analytics/analytics.service';
import { isDeclinedOnboardingStatus } from '@Utils/onboardingStatus';
import type { RegionCode } from 'google-libphonenumber';
import type { OnboardingStatus } from '@Store/index';
import type {
  BusinessStatus,
  CustomerData,
  IAvailablePromoCodes,
} from '@Store/slices/customer/customer.types';

export interface LoginResponse {
  externalId: string;
  onboardingStatus: OnboardingStatus;
  mfaDestination?: string;
  mfaType?: string;
  stateToken?: string;
  onboardingTypeEnum?: OnboardingTypeEnum;
  nameTooLong?: boolean;
  allowEditingPersonalDetails?: boolean;
  hasSoleProprietor?: boolean;
  availablePromoCodes?: IAvailablePromoCodes;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  bank?: any;
  isNonUsSupported?: boolean;
  idCountry?: string;
  nationalIdDescription?: string;
  campaignName?: string;
  customerData?: CustomerData;
  lastStepData?: {
    stepName: string;
    version: string;
  };
  phoneCountryCode?: RegionCode;
  business?: {
    status?: BusinessStatus;
  };
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface HandleLoginResponseOptions {
  loginResponse: LoginResponse;
  email?: string;
  password?: string;
  onMfaRequired?: (response: LoginResponse) => void;
  onMfaWithoutStateToken?: (response: LoginResponse) => void;
  onSuccess?: (response: LoginResponse) => void;
  onResume?: () => void;
  loginToken?: string;
}

// @TODO: Make this generic for all the login response places (welcome page, login page, loginMfaVeriication)

export const useLoginMutation = () => {
  return useMutation({
    mutationFn: async (data: LoginCredentials) => {
      const response = await HttpService.post('v5/onboarding/login', {
        email: data.email,
        password: data.password,
      });
      return response.data as LoginResponse;
    },
    mutationKey: ['login'],
  });
};

export const handleLoginResponse = async ({
  loginResponse,
  password,
  onMfaRequired,
  onMfaWithoutStateToken,
  onSuccess,
  onResume,
  loginToken,
}: HandleLoginResponseOptions) => {
  const customerData = useBoundStore.getState().getCustomerData();
  const setOnboardingIdentifier = useBoundStore
    .getState()
    .onboardingSlice.getOnboardingData().setOnboardingIdentifier;

  // Handle multi-business onboarding type
  if (
    loginResponse?.onboardingTypeEnum &&
    loginResponse?.onboardingTypeEnum === OnboardingTypeEnum.MULTI_BUSINESS
  ) {
    setOnboardingIdentifier(OnboardingFlowIdentifier.DIRECT_MULTI_BUSINESS);
  }

  // Handle accountant-as-owner onboarding type
  if (
    loginResponse?.onboardingTypeEnum &&
    loginResponse?.onboardingTypeEnum === OnboardingTypeEnum.ACCOUNTANT_AS_OWNER
  ) {
    setOnboardingIdentifier(OnboardingFlowIdentifier.ACCOUNTANT_AS_OWNER);
  }

  // Declined customers have no valid token and cannot continue onboarding.
  // Route them to the KYC failed page instead of the MFA / recovery fallbacks.
  const isDeclined = isDeclinedOnboardingStatus(loginResponse?.onboardingStatus);

  // Handle MFA with state token
  if (
    !isDeclined &&
    loginResponse?.mfaDestination &&
    loginResponse?.mfaType &&
    loginResponse?.stateToken
  ) {
    if (onMfaRequired) {
      onMfaRequired(loginResponse);
      return;
    }
  }

  // Handle MFA without state token
  if (!isDeclined && loginResponse?.mfaDestination && loginResponse?.mfaType) {
    if (onMfaWithoutStateToken) {
      onMfaWithoutStateToken(loginResponse);
      return;
    }
  }

  // Handle successful login - update customer data
  const customerDataToUpdate = { ...customerData };
  customerDataToUpdate.externalId = loginResponse.externalId;
  customerDataToUpdate.customerId = loginResponse.externalId;
  customerDataToUpdate.accessToken = loginToken || '';
  customerDataToUpdate.onboardingStatus = loginResponse.onboardingStatus;

  if (password) {
    customerDataToUpdate.customerData.password = password;
  }

  if (loginResponse?.onboardingTypeEnum) {
    customerDataToUpdate.customerData.onboardingTypeEnum = loginResponse.onboardingTypeEnum;
  }

  if (loginResponse?.business?.status) {
    customerDataToUpdate.business = {
      status: loginResponse.business.status,
    };
  }

  customerDataToUpdate.nameTooLong = loginResponse.nameTooLong;
  customerDataToUpdate.allowEditingPersonalDetails = loginResponse.allowEditingPersonalDetails;
  customerDataToUpdate.hasSoleProprietor = loginResponse.hasSoleProprietor;
  customerDataToUpdate.availablePromoCodes = loginResponse.availablePromoCodes;
  customerDataToUpdate.bank = loginResponse.bank;
  customerDataToUpdate.isNonUsSupported = loginResponse.isNonUsSupported;
  customerDataToUpdate.idCountry = loginResponse.idCountry;
  customerDataToUpdate.nationalIdDescription = loginResponse.nationalIdDescription;
  customerDataToUpdate.campaignName = loginResponse.campaignName;
  customerDataToUpdate.customerData = {
    ...customerDataToUpdate.customerData,
    ...loginResponse.customerData,
  };

  customerDataToUpdate.customerData.lastStepData = loginResponse?.lastStepData?.stepName
    ? {
        version: Number(loginResponse.lastStepData.version || 0),
        stepName: loginResponse.lastStepData.stepName,
      }
    : undefined;
  customerDataToUpdate.customerData.phoneCountryCode = loginResponse.phoneCountryCode as RegionCode;

  updateCustomerDataFromServer(customerDataToUpdate, customerData);
  useBoundStore.getState().setOnboardingAuthenticated(true);
  evaluateRainguard();
  identifyLogRocketUser(loginResponse.externalId, {
    isReturningUser: true,
    onboardingStatus: loginResponse.onboardingStatus,
    onboardingTypeEnum: loginResponse.onboardingTypeEnum,
  });
  identifyGA4User(loginResponse.externalId);
  setGA4UserProperties();
  await buildNavigationStack({ shouldHandleLoginMannually: true, onResume });

  if (onSuccess) {
    onSuccess(loginResponse);
  }
};
